import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { v4 as uuidv4 } from 'uuid';
import {
  MomentScheduleItemFieldsFragment,
  MomentTemplateFieldsFragment,
  MomentType,
} from '@generated/types';
import { MomentImage } from '@components/Analytics/Service/AnalyticsServiceGrid';
import { TFunction } from 'next-i18next';

dayjs.extend(duration);

export interface MomentScheduleItem {
  id: string;
  startTimeOffset: number | null | undefined;
  ordinal: number | null;
  startTimeOffsetInput: string | undefined;
  startTimeOffsetError: boolean;
  momentTemplate: MomentTemplateFieldsFragment;
}

export const getTitle = (type: MomentType): string => {
  switch (type) {
    case MomentType.CONNECT:
      return 'Connect';
    case MomentType.GIVING:
      return 'Give';
    case MomentType.INVITE:
      return 'Invite';
    case MomentType.IMAGE:
      return 'Image';
    case MomentType.PRAYER:
      return 'Prayer';
    case MomentType.QUOTE:
      return 'Quote';
    case MomentType.RESPONSE:
      return 'Response';
    case MomentType.SALVATION:
      return 'Salvation';
    case MomentType.SIGNUP:
      return 'Signup';
    case MomentType.NEXT_STEPS:
      return 'Next Steps';
  }
};

export const getLocalizedTitle = (type: MomentType, t: TFunction): string => {
  switch (type) {
    case MomentType.CONNECT:
      return t('connect');
    case MomentType.GIVING:
      return t('give');
    case MomentType.INVITE:
      return t('invite');
    case MomentType.IMAGE:
      return t('image');
    case MomentType.PRAYER:
      return t('prayer');
    case MomentType.QUOTE:
      return t('quote');
    case MomentType.RESPONSE:
      return t('response');
    case MomentType.SALVATION:
      return t('salvation');
    case MomentType.SIGNUP:
      return t('signup');
    case MomentType.NEXT_STEPS:
      return t('next_steps');
  }
};

export const getContent = (
  momentTemplate: MomentTemplateFieldsFragment | MomentScheduleItemFieldsFragment | undefined
): string | undefined => {
  switch (momentTemplate?.__typename) {
    case 'ConnectMoment':
      return momentTemplate?.options.text;
    case 'GivingMoment':
      return momentTemplate?.options.text;
    case 'InviteMoment':
      return momentTemplate?.options.text;
    case 'PrayerMoment':
      return momentTemplate?.options.text;
    case 'QuoteMoment':
      return momentTemplate?.options.text;
    case 'ResponseMoment':
      return momentTemplate?.options.text;
    case 'SalvationMoment':
      return momentTemplate?.options.labels?.prompt || 'I commit my life to Jesus.'; // TODO: translate
    case 'ImageMoment':
      return momentTemplate?.options.altText ?? '';
    case 'NextStepsMoment':
      return momentTemplate?.options.text;
  }
};

export const getImage = (
  momentTemplate: MomentTemplateFieldsFragment | MomentScheduleItemFieldsFragment
): MomentImage => {
  if (momentTemplate.__typename === 'ImageMoment') {
    return {
      url: momentTemplate?.options?.imageUrl ?? '',
      altText: momentTemplate?.options?.altText ?? '',
      shareText: momentTemplate?.options?.shareText ?? '',
    };
  }
  return {
    url: '',
    altText: '',
    shareText: '',
  };
};

export const getAttribution = (
  momentTemplate: MomentTemplateFieldsFragment | MomentScheduleItemFieldsFragment
): string | null | undefined => {
  if (momentTemplate.__typename === 'QuoteMoment') {
    return momentTemplate?.options?.attribution;
  }

  return null;
};

export const transformStartTimeOffset = (
  startTimeOffset: number | null | undefined
): string | undefined => {
  if (startTimeOffset === null) return;
  const displayStartTimeOffset = new Date((startTimeOffset || 0) * 1000)
    .toISOString()
    .substr(11, 8);
  return displayStartTimeOffset;
};

export const numericStartTimeOffset = (
  displayStartTimeOffset: string | undefined
): number | undefined => {
  if (!displayStartTimeOffset) return;

  const [hours, minutes, seconds] = displayStartTimeOffset.split(':');

  // @ts-expect-error - Unsure why TS is complaining about this, it works
  const dur = dayjs.duration({
    hours,
    minutes,
    seconds,
  });

  return dur.asSeconds();
};

export const formatStartTimeOffset = (
  displayStartTimeOffset: string | undefined
): string | undefined => {
  if (!displayStartTimeOffset) return;
  const paddedDigits = displayStartTimeOffset.replace(/\D/g, '').padStart(6, '0');
  return `${paddedDigits.slice(0, 2)}:${paddedDigits.slice(2, 4)}:${paddedDigits.slice(4, 6)}`;
};

export const buildMomentScheduleItems = (
  items: Array<MomentScheduleItemFieldsFragment>
): Array<MomentScheduleItem> =>
  items.map(i => ({
    id: i.id ? i.id : uuidv4(),
    startTimeOffset: i.startTimeOffset,
    ordinal: i.ordinal || null,
    startTimeOffsetInput: transformStartTimeOffset(i.startTimeOffset),
    startTimeOffsetError: false,
    momentTemplate: i?.momentTemplate,
  }));
